import { useMemo, useState } from 'react';
import { Card, Checkbox, InlineStack, Select, SkeletonBodyText, Text } from '@shopify/polaris';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { MONTH_LABELS, formatCompactCurrency, heatColor, toNumber } from './analyticsHelpers';

const cellStyle = {
  padding: '6px 8px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--p-color-border-secondary, #ebebeb)'
};

const MODE_CONFIG = {
  MONTH: { labels: MONTH_LABELS, monthsPerBucket: 1 },
  QUARTER: { labels: ['Q1', 'Q2', 'Q3', 'Q4'], monthsPerBucket: 3 },
  HALF: { labels: ['H1 (Jan–Jun)', 'H2 (Jul–Dec)'], monthsPerBucket: 6 },
  YEAR: { labels: ['Year total'], monthsPerBucket: 12 }
};

// Heatmap of a metric per year × period (month/quarter/half year/year); darker = bigger.
// The cumulative toggle turns cells into the running year total through that period,
// colored per column so the same point in the year is comparable across years.
// Pass negativeColorRgb for metrics that can go below zero (margins): negative cells
// get their own scale in that color. Pass onPeriodClick(period) to make cells drillable.
export default function YearMonthMatrix({ title, subtitle, rows, valueKey, colorRgb = '46, 125, 50', negativeColorRgb = null, loading = false, onPeriodClick }) {
  const [mode, setMode] = useState('MONTH');
  const [cumulative, setCumulative] = useState(false);
  const config = MODE_CONFIG[mode];
  const bucketCount = config.labels.length;

  const matrix = useMemo(() => {
    const perPeriod = {};
    (rows || []).forEach((row) => {
      const y = Number(row.year);
      const m = Number(row.month);
      if (!Number.isFinite(y) || !Number.isFinite(m)) return;
      const bucket = Math.floor((m - 1) / config.monthsPerBucket);
      perPeriod[y] = perPeriod[y] || Array(bucketCount).fill(0);
      perPeriod[y][bucket] += toNumber(row[valueKey]);
    });

    const years = Object.keys(perPeriod)
      .map(Number)
      .sort((a, b) => b - a);
    const now = new Date();

    // null = future bucket of the running year (nothing meaningful to show there)
    const cells = {};
    years.forEach((y) => {
      let running = 0;
      cells[y] = perPeriod[y].map((value, idx) => {
        const isFuture = y === now.getFullYear() && new Date(y, idx * config.monthsPerBucket, 1) > now;
        if (isFuture) return null;
        running += value;
        return cumulative ? running : value;
      });
    });

    let globalMax = 0;
    let globalMaxNeg = 0;
    const columnMax = Array(bucketCount).fill(0);
    const columnMaxNeg = Array(bucketCount).fill(0);
    years.forEach((y) => {
      cells[y].forEach((value, idx) => {
        if (value === null) return;
        if (value >= 0) {
          globalMax = Math.max(globalMax, value);
          columnMax[idx] = Math.max(columnMax[idx], value);
        } else {
          globalMaxNeg = Math.max(globalMaxNeg, -value);
          columnMaxNeg[idx] = Math.max(columnMaxNeg[idx], -value);
        }
      });
    });

    // benchmark row: average over the 5 most recent completed years with data, so
    // neither the small early years nor the running partial year skew the target
    const columnAverages = config.labels.map((_, idx) => {
      const values = years
        .filter((y) => y !== now.getFullYear())
        .map((y) => cells[y][idx])
        .filter((value) => value !== null && value !== 0)
        .slice(0, 5);
      if (!values.length) return 0;
      return values.reduce((a, b) => a + b, 0) / values.length;
    });

    const yearTotals = {};
    years.forEach((y) => {
      yearTotals[y] = perPeriod[y].reduce((a, b) => a + b, 0);
    });

    return { cells, years, globalMax, globalMaxNeg, columnMax, columnMaxNeg, columnAverages, yearTotals };
  }, [rows, valueKey, config, bucketCount, cumulative]);

  const cellBackground = (value, idx) => {
    if (!value) return 'transparent';
    if (value > 0) return heatColor(value, cumulative ? matrix.columnMax[idx] : matrix.globalMax, colorRgb);
    if (!negativeColorRgb) return 'transparent';
    return heatColor(-value, cumulative ? matrix.columnMaxNeg[idx] : matrix.globalMaxNeg, negativeColorRgb);
  };

  const periodForBucket = (year, idx) => {
    if (mode === 'YEAR') return { year };
    if (cumulative) {
      const endMonth = (idx + 1) * config.monthsPerBucket;
      return endMonth === 12 ? { year } : { year, through_month: endMonth };
    }
    if (mode === 'MONTH') return { year, month: idx + 1 };
    if (mode === 'QUARTER') return { year, quarter: idx + 1 };
    return { year, half: idx + 1 };
  };

  const clickableProps = (period, hasValue) => {
    if (!onPeriodClick || !hasValue) return {};
    return {
      onClick: () => onPeriodClick(period),
      title: 'Click to see the breakdown',
      style: { cursor: 'pointer' },
      role: 'button'
    };
  };

  const showTotalColumn = mode !== 'YEAR';

  return (
    <Card>
      <CardHeading title={title} />
      {subtitle ? (
        <Text tone="subdued" as="p">
          {subtitle}
        </Text>
      ) : null}
      <div style={{ margin: '12px 0' }}>
        <InlineStack gap="400" blockAlign="center">
          <div style={{ width: '160px' }}>
            <Select
              label="View"
              labelHidden
              options={[
                { label: 'Per month', value: 'MONTH' },
                { label: 'Per quarter', value: 'QUARTER' },
                { label: 'Per half year', value: 'HALF' },
                { label: 'Per year', value: 'YEAR' }
              ]}
              value={mode}
              onChange={setMode}
            />
          </div>
          <Checkbox label="Cumulative per year" checked={cumulative} onChange={setCumulative} />
        </InlineStack>
      </div>
      {loading ? (
        <SkeletonBodyText lines={8} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={{ ...cellStyle, textAlign: 'left' }}>Year</th>
                {config.labels.map((label) => (
                  <th key={label} style={cellStyle}>
                    {label}
                  </th>
                ))}
                {showTotalColumn ? <th style={cellStyle}>Total</th> : null}
              </tr>
            </thead>
            <tbody>
              {matrix.years.map((year) => (
                <tr key={year}>
                  <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}>{year}</td>
                  {matrix.cells[year].map((value, idx) => {
                    const clickable = clickableProps(periodForBucket(year, idx), value !== null && value !== 0);
                    return (
                      <td key={idx} {...clickable} style={{ ...cellStyle, backgroundColor: cellBackground(value, idx), ...(clickable.style || {}) }}>
                        {value ? formatCompactCurrency(value) : '—'}
                      </td>
                    );
                  })}
                  {showTotalColumn
                    ? (() => {
                        const yearTotal = matrix.yearTotals[year];
                        const clickable = clickableProps({ year }, yearTotal !== 0);
                        return (
                          <td {...clickable} style={{ ...cellStyle, fontWeight: 600, ...(clickable.style || {}) }}>
                            {formatCompactCurrency(yearTotal)}
                          </td>
                        );
                      })()
                    : null}
                </tr>
              ))}
              <tr>
                <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{cumulative ? 'Avg cumulative (last 5 yrs)' : 'Avg / period (last 5 yrs)'}</td>
                {matrix.columnAverages.map((value, idx) => (
                  <td key={idx} style={{ ...cellStyle, fontWeight: 600 }}>
                    {value ? formatCompactCurrency(value) : '—'}
                  </td>
                ))}
                {showTotalColumn ? <td style={cellStyle} /> : null}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {onPeriodClick ? (
        <div style={{ marginTop: '8px' }}>
          <Text tone="subdued" as="p" variant="bodySm">
            Click a cell or a year total to see the breakdown behind it. In cumulative view the &ldquo;Avg cumulative&rdquo; row tells you where a normal year stands at that point.
          </Text>
        </div>
      ) : null}
    </Card>
  );
}
